import React, { useState, useEffect, useRef } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Button from 'Components/DesignSystem/Button/Button';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { Cross, AlertTriangle } from 'Components/DesignSystem/Iconography/Icons';

type RenameIterationDialogProps = {
    isOpen: boolean;
    currentName: number;
    existingNames: number[];
    onSave: (newName: number) => void;
    onCancel: () => void;
};

/**
 * Rename dialog on the DS `Dialog` (2026-09-08). Behaviour byte-identical:
 * the falsy-zero validation (`!value || value <= 0` — 1-indexed names, `0` never
 * passes; see the must-not-regress note in okf/computed-substitutions/e2e.md),
 * Enter-to-save from anywhere, outside-click and Escape cancel, and focus+select
 * on open. Escape moved OFF this component's own listener onto the Dialog's
 * dismiss stack — keeping both would fire `onCancel` twice per keypress (the
 * double-close hazard `useDismissable`'s docblock warned about for exactly this
 * file). The "Iteration " prefix is composed around a labelless `TextField`
 * (the DS field has no adornment slot yet).
 */
const RenameIterationDialog: React.FC<RenameIterationDialogProps> = ({
    isOpen,
    currentName,
    existingNames,
    onSave,
    onCancel
}) => {
    const [inputValue, setInputValue] = useState<number>(currentName);
    const [validationError, setValidationError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Update input value when currentName changes
    useEffect(() => {
        setInputValue(currentName);
        setValidationError('');
    }, [currentName]);

    // Focus input when dialog opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isOpen]);

    // Validate input value
    const validateInput = (value: number): string => {
        if (!value || value <= 0) {
            return 'Please enter a valid iteration number';
        }
        if (value !== currentName && existingNames.includes(value)) {
            return 'Iteration with the same name already exists';
        }

        return '';
    };

    // Enter saves from anywhere in the dialog; Escape is the Dialog's own dismiss.
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return;

            if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, inputValue, onCancel]);

    const handleSave = () => {
        const error = validateInput(inputValue);

        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError('');
        onSave(inputValue);
    };

    const handleInputChange = (value: string) => {
        const parsed = parseInt(value) || 0;
        setInputValue(parsed);

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    const hasError = !!validationError;
    const isValidInput = inputValue > 0 && !hasError;

    if (!isOpen) return null;

    return (
        <Dialog
            isOpen
            onClose={onCancel}
            title="Rename Iteration"
            size="sm"
            closeOnOutsideClick
            data-testid="rename-iteration-dialog"
            showCloseButton={false}
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    onClick={onCancel}
                    data-testid="close-dialog-button"
                >
                    <Cross size={16} />
                </IconButton>
            }
            footer={
                <div className="tw-flex tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        onClick={onCancel}
                        data-testid="cancel-button"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={!isValidInput}
                        data-testid="save-button"
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-2">
                <FieldTitle label="Iteration Name" htmlFor="iteration-name-input" />
                <div className="tw-relative">
                    <TextField
                        ref={inputRef}
                        id="iteration-name-input"
                        label=""
                        type="text"
                        inputMode="numeric"
                        value={inputValue === 0 ? '' : inputValue.toString()}
                        onChange={handleInputChange}
                        error={hasError}
                        style={{ paddingLeft: '5.25rem' }}
                        data-testid="iteration-name-input"
                    />
                    <div className="tw-absolute tw-inset-y-0 tw-left-3 tw-flex tw-items-center tw-pointer-events-none">
                        <span className="tw-text-ink-tertiary tw-text-sm">
                            Iteration{' '}
                        </span>
                    </div>
                </div>

                {/* Error message */}
                {validationError && (
                    <div className="tw-flex tw-items-center tw-gap-2" data-testid="validation-error">
                        <span className="tw-text-tone-danger-glyph tw-flex"><AlertTriangle size={16} /></span>
                        <span className="tw-text-tone-danger-ink tw-text-sm tw-font-normal">
                            {validationError}
                        </span>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default RenameIterationDialog;
